import { useState, useMemo } from "react";
import { Card, Radio, DatePicker, Table, Tag, Spin, Empty } from "antd";
import dayjs from "dayjs";
import { formatDate } from "../_shared/utils/dateUtils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";
import Header from "../_shared/components/Header";
import {
  useGetCapacityQuery,
  useGetCapacityAnalyticsQuery,
} from "../../../redux/api/scheduling";

const PALETTE = [
  "#1677ff",
  "#52c41a",
  "#fa8c16",
  "#eb2f96",
  "#722ed1",
  "#13c2c2",
  "#faad14",
  "#2f54eb",
  "#f5222d",
  "#a0d911",
];

function lighten(hex: string, amount = 0.45): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return `#${lr.toString(16).padStart(2, "0")}${lg.toString(16).padStart(2, "0")}${lb.toString(16).padStart(2, "0")}`;
}

function fmtDate(iso: string) {
  return formatDate(iso, "MM/DD");
}

function minutesToHours(m: number) {
  return Math.round((m / 60) * 10) / 10;
}

function renderMachineTime(groupData: any) {
  if (!groupData) return "—";
  const raw = Math.round((groupData.total / 60) * 10) / 10;
  const effective = Math.round(((groupData.effective || 0) / 60) * 10) / 10;
  const used = Math.round((groupData.allocated / 60) * 10) / 10;
  const pct = effective > 0 ? Math.round((used / effective) * 100) : 0;
  return (
    <span>
      {used}h / {effective}h / {raw}h
      <Tag
        color={pct > 90 ? "red" : pct > 70 ? "orange" : "green"}
        className="ml-2"
      >
        {pct}%
      </Tag>
    </span>
  );
}

export default function Capacity() {
  const [view, setView] = useState("rolling4");
  const [schedulePass, setSchedulePass] = useState("top_down");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));

  const { data: tableData, isLoading: tableLoading } = useGetCapacityQuery(
    { view, pass: schedulePass, date },
    { refetchOnMountOrArgChange: true },
  );
  const { data: chartData, isLoading: chartLoading } =
    useGetCapacityAnalyticsQuery(
      { view, pass: schedulePass, date },
      { refetchOnMountOrArgChange: true },
    );

  const periods = tableData?.periods || [];
  const series = chartData?.series || [];
  const isLoading = tableLoading || chartLoading;

  // ---- Table helpers ----
  const tableGroupNames = useMemo(() => {
    const names: { group_id: number | null; group_name: string }[] = [];
    const seen = new Set<string>();
    for (const p of periods) {
      for (const g of p.machine_time_by_group || []) {
        const key = g.group_id !== null ? String(g.group_id) : "ungrouped";
        if (!seen.has(key)) {
          seen.add(key);
          names.push({ group_id: g.group_id, group_name: g.group_name });
        }
      }
    }
    return names;
  }, [periods]);

  const baseColumns: any[] = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      fixed: "left" as const,
      width: 100,
      render: (v: string) => formatDate(v, "ddd MM/DD"),
    },
    {
      title: "Slots (Used / Total)",
      key: "slots",
      width: 150,
      render: (_: any, r: any) => {
        const pct =
          r.slots.total > 0
            ? Math.round((r.slots.allocated / r.slots.total) * 100)
            : 0;
        return (
          <span>
            {r.slots.allocated} / {r.slots.total}
            <Tag
              color={pct > 90 ? "red" : pct > 70 ? "orange" : "green"}
              className="ml-2"
            >
              {pct}%
            </Tag>
          </span>
        );
      },
    },
    {
      title: "Staff Time (Used / Total hrs)",
      key: "staff_time",
      width: 170,
      render: (_: any, r: any) => {
        const total = Math.round((r.staff_time_minutes.total / 60) * 10) / 10;
        const used =
          Math.round((r.staff_time_minutes.allocated / 60) * 10) / 10;
        const pct = total > 0 ? Math.round((used / total) * 100) : 0;
        return (
          <span>
            {used}h / {total}h
            <Tag
              color={pct > 90 ? "red" : pct > 70 ? "orange" : "green"}
              className="ml-2"
            >
              {pct}%
            </Tag>
          </span>
        );
      },
    },
  ];

  const groupColumns = tableGroupNames.map((g) => ({
    title: `${g.group_name} (Used / Eff / Raw)`,
    key: `group_${g.group_id ?? "ungrouped"}`,
    width: 220,
    render: (_: any, r: any) => {
      const groupData = (r.machine_time_by_group || []).find(
        (gd: any) => gd.group_id === g.group_id
      );
      return renderMachineTime(groupData);
    },
  }));

  const columns = [...baseColumns, ...groupColumns];

  // ---- Chart data helpers ----
  const taskIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of series) {
      for (const tid of Object.keys(p.slots_by_task || {})) {
        ids.add(tid);
      }
    }
    return Array.from(ids).sort();
  }, [series]);

  const taskNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of series) {
      for (const [tid, info] of Object.entries(p.slots_by_task || {})) {
        if ((info as any).task_name) map[tid] = (info as any).task_name;
      }
    }
    return map;
  }, [series]);

  const slotUtilData = useMemo(
    () =>
      series.map((p: any) => {
        const row: any = { date: fmtDate(p.date) };
        for (const tid of taskIds) {
          const info = p.slots_by_task?.[tid];
          const total = info?.total || 0;
          const used = info?.allocated || 0;
          row[`${tid}_pct`] = total > 0 ? Math.round((used / total) * 100) : 0;
        }
        return row;
      }),
    [series, taskIds]
  );

  const staffTimeData = useMemo(
    () =>
      series.map((p: any) => ({
        date: fmtDate(p.date),
        total: minutesToHours(p.staff_time?.total_minutes || 0),
        allocated: minutesToHours(p.staff_time?.allocated_minutes || 0),
      })),
    [series]
  );

  const chartGroupNames = useMemo(() => {
    const names: { id: number | null; name: string }[] = [];
    const seen = new Set<string>();
    for (const p of series) {
      for (const g of p.machine_time_by_group || []) {
        const key = g.group_id !== null ? String(g.group_id) : "ungrouped";
        if (!seen.has(key)) {
          seen.add(key);
          names.push({ id: g.group_id, name: g.group_name });
        }
      }
    }
    return names;
  }, [series]);

  const machineTimeData = useMemo(
    () =>
      series.map((p: any) => {
        const row: any = { date: fmtDate(p.date) };
        for (const g of p.machine_time_by_group || []) {
          const key = g.group_id !== null ? String(g.group_id) : "ungrouped";
          row[`${key}_eff`] = minutesToHours(g.effective_minutes || 0);
          row[`${key}_alloc`] = minutesToHours(g.allocated_minutes || 0);
        }
        return row;
      }),
    [series]
  );

  const machineUtilPctData = useMemo(
    () =>
      series.map((p: any) => {
        const row: any = { date: fmtDate(p.date) };
        for (const g of p.machine_time_by_group || []) {
          const key = g.group_id !== null ? String(g.group_id) : "ungrouped";
          const eff = g.effective_minutes || 0;
          const alloc = g.allocated_minutes || 0;
          row[`${key}_pct`] = eff > 0 ? Math.round((alloc / eff) * 100) : 0;
        }
        return row;
      }),
    [series]
  );

  return (
    <div className="w-full p-4">
      <Header title="Capacity" />

      <Card className="mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <span className="mr-2 font-medium">View:</span>
            <Radio.Group
              value={view}
              onChange={(e) => setView(e.target.value)}
            >
              <Radio.Button value="daily">Daily</Radio.Button>
              <Radio.Button value="weekly">Weekly</Radio.Button>
              <Radio.Button value="rolling4">Rolling 4 Weeks</Radio.Button>
              <Radio.Button value="monthly">Monthly</Radio.Button>
            </Radio.Group>
          </div>
          <div>
            <span className="mr-2 font-medium">Schedule:</span>
            <Radio.Group
              value={schedulePass}
              onChange={(e) => setSchedulePass(e.target.value)}
            >
              <Radio.Button value="top_down">Top Down</Radio.Button>
              <Radio.Button value="bottom_up">Bottom Up</Radio.Button>
            </Radio.Group>
          </div>
          <div>
            <span className="mr-2 font-medium">Date:</span>
            <DatePicker
              value={dayjs(date)}
              onChange={(d) => d && setDate(d.format("YYYY-MM-DD"))}
            />
          </div>
        </div>
      </Card>

      {isLoading ? (
        <Spin size="large" className="mt-10 mx-auto block" />
      ) : series.length === 0 && periods.length === 0 ? (
        <Empty description="No capacity data for this range" />
      ) : (
        <>
          {/* ---- Charts ---- */}
          {series.length > 0 && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
              {/* Staff Slot Utilization (%) */}
              <Card title="Staff Slot Utilization (%)" size="small">
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={slotUtilData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis
                      domain={[0, 100]}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip formatter={(v: number) => `${v}%`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {taskIds.map((tid, i) => (
                      <Line
                        key={tid}
                        type="monotone"
                        dataKey={`${tid}_pct`}
                        name={taskNameMap[tid] || tid}
                        stroke={PALETTE[i % PALETTE.length]}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Card>

              {/* Staff Time (hours) */}
              <Card title="Staff Time (hours)" size="small">
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart data={staffTimeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area
                      type="monotone"
                      dataKey="total"
                      name="Available"
                      stroke="#1677ff"
                      fill="#1677ff"
                      fillOpacity={0.15}
                    />
                    <Area
                      type="monotone"
                      dataKey="allocated"
                      name="Allocated"
                      stroke="#52c41a"
                      fill="#52c41a"
                      fillOpacity={0.4}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              {/* Machine Time by Group (hours) – line chart */}
              <Card title="Machine Time by Group (hours)" size="small">
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={machineTimeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {chartGroupNames.map((g, i) => {
                      const key =
                        g.id !== null ? String(g.id) : "ungrouped";
                      const baseColor = PALETTE[i % PALETTE.length];
                      return [
                        <Line
                          key={`${key}_eff`}
                          type="monotone"
                          dataKey={`${key}_eff`}
                          name={`${g.name} (available)`}
                          stroke={lighten(baseColor)}
                          strokeWidth={2}
                          strokeDasharray="5 3"
                          dot={false}
                        />,
                        <Line
                          key={`${key}_alloc`}
                          type="monotone"
                          dataKey={`${key}_alloc`}
                          name={`${g.name} (allocated)`}
                          stroke={baseColor}
                          strokeWidth={2}
                          dot={false}
                        />,
                      ];
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </Card>

              {/* Machine Utilization (%) – line chart */}
              <Card title="Machine Utilization (%)" size="small">
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={machineUtilPctData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis
                      domain={[0, 100]}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip formatter={(v: number) => `${v}%`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {chartGroupNames.map((g, i) => {
                      const key =
                        g.id !== null ? String(g.id) : "ungrouped";
                      return (
                        <Line
                          key={`${key}_pct`}
                          type="monotone"
                          dataKey={`${key}_pct`}
                          name={g.name}
                          stroke={PALETTE[i % PALETTE.length]}
                          strokeWidth={2}
                          dot={false}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </div>
          )}

          {/* ---- Day-by-Day Table ---- */}
          <Table
            dataSource={periods}
            columns={columns}
            rowKey="date"
            size="small"
            pagination={false}
            scroll={{ x: "max-content" }}
          />
        </>
      )}
    </div>
  );
}
